import * as React from "react"

import { cn } from "@/lib/utils"

// Phone numbers always read left to right (RTL-06): a value with no letters
// ("+971 50 123 4567") would otherwise reorder its digit groups.
const LEFT_TO_RIGHT_TYPES = new Set(["tel"])
// Text whose direction comes from what is typed: free text in either script,
// and email, URL and password values, which start with a Latin letter.
const CONTENT_DIRECTION_TYPES = new Set(["text", "search", "email", "url", "password"])

/**
 * Typed text gets `unicode-bidi: plaintext`, not `dir="auto"` or `dir="ltr"`:
 * each line takes the direction of its first letter, so English typed on an
 * Arabic page reads left to right ("user@" never becomes "@user") and the
 * reverse, while the field itself keeps the page's direction. That matters
 * twice (both measured in Chromium, 2026-09-26):
 *  - an empty `dir="auto"` field is left to right even on an Arabic page,
 *    which puts the placeholder and caret on the wrong side;
 *  - `dir` also decides where the field's own start and end are. A password
 *    field forced to `dir="ltr"` on an Arabic page put its `pe-10` room on
 *    the right while the eye beside it (`end-3`, placed by the page's
 *    direction) sat on the left, over the text. With the page's direction,
 *    the room and the eye (or an email field's icon) are on the same side.
 * Only phone numbers are forced left to right. An explicit `dir` always wins.
 */
function Input({ className, type, dir, ...props }: React.ComponentProps<"input">) {
  const contentDirection = dir === undefined && (type === undefined || CONTENT_DIRECTION_TYPES.has(type))
  return (
    <input
      type={type}
      dir={dir ?? (type !== undefined && LEFT_TO_RIGHT_TYPES.has(type) ? "ltr" : undefined)}
      data-slot="input"
      className={cn(
        "h-9 w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none selection:bg-primary selection:text-primary-foreground file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm dark:bg-input/30",
        "focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
        "aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40",
        contentDirection && "[unicode-bidi:plaintext]",
        className
      )}
      {...props}
    />
  )
}

export { Input }
